import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import SkudPassesBlock from '@/components/SkudPassesBlock';

/**
 * Модалка создания/редактирования человека в СКУД.
 *
 * Пропс mode:
 *   • 'create'          → POST /api/skud/persons
 *   • 'edit' + person   → PUT  /api/skud/persons/{person.skudId}
 *
 * Все изменения сохраняются локально с pending_sync=1. Node outbound-воркер
 * пошлёт в Orion `addperson`/`updatePerson` в ближайшие 10 секунд, а после
 * `addperson` подменит отрицательный skud_id на реальный (плюс перепривяжет
 * users.skud_person_id / students.skud_person_id / skud_passes.person_skud_id).
 *
 * Пропс `role` (student|employee|parent) — берётся из пикера, где вызвана
 * модалка. По нему выставляем дефолтное подразделение из настроек СКУД,
 * чтобы админ не выбирал вручную каждый раз.
 *
 * После успешного create модалка НЕ закрывается — переходит в edit-режим
 * и показывает блок пропусков, чтобы админ мог тут же выдать карту, не
 * выходя из окна. Пикер / список персон получают onSaved и обновляются
 * в фоне; модалку пользователь закрывает вручную.
 *
 * Возвращает через onSaved({ skudId, lastName, firstName, middleName,
 * isDismissed, pendingSync }) — пикер / страница персон этим обновляют
 * список / привязку.
 */
export default function SkudPersonEditModal({ mode, person, role, onClose, onSaved }) {
  // Локальный skudId сохранённой персоны — заполняется при open в edit
  // и появляется после успешного POST в create. От него зависят UI-режим
  // и рендер блока пропусков.
  const [savedSkudId, setSavedSkudId] = useState(person?.skudId ?? null);
  const isEditNow = savedSkudId != null;

  const [lastName,   setLastName]   = useState(person?.lastName   || '');
  const [firstName,  setFirstName]  = useState(person?.firstName  || '');
  const [middleName, setMiddleName] = useState(person?.middleName || '');
  const [isDismissed, setDismissed] = useState(Boolean(person?.isDismissed));
  const [departmentSkudId, setDepartment] = useState(
    person?.departmentSkudId != null ? String(person.departmentSkudId) : ''
  );
  const [tabNumber, setTabNumber] = useState(person?.tabNumber || '');
  const [guid1c,    setGuid1c]    = useState(person?.guid1c    || '');

  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [savedMsg, setSavedMsg] = useState(null); // краткий тост после сохранения

  // Подразделения из кэша + дефолт для роли.
  // При create дефолтим по role → одно из настроек СКУД. При edit
  // подставилось из person.departmentSkudId в initial state — не трогаем.
  useEffect(() => {
    let aborted = false;
    Promise.all([
      api.get('/api/skud/departments').catch(() => ({ rows: [] })),
      // /api/settings/skud требует settings.skud/read; у обычных операторов
      // этого права нет — тихо получаем null и просто не подставляем дефолт.
      mode === 'edit' ? Promise.resolve(null) : api.get('/api/settings/skud').catch(() => null),
    ]).then(([d, s]) => {
      if (aborted) return;
      setDepartments(d.rows || []);
      if (mode !== 'edit' && s && role) {
        const key = role === 'parent'   ? 'department_parents_skud_id'
                  : role === 'employee' ? 'department_employees_skud_id'
                  : role === 'student'  ? 'department_students_skud_id'
                  : null;
        const defId = key ? s.value?.[key] : null;
        if (defId != null && defId !== '' && departmentSkudId === '') {
          setDepartment(String(defId));
        }
      }
    });
    return () => { aborted = true; };
    // departmentSkudId — намеренно не в deps: заполняем один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, role]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (lastName.trim()  === '') { setError('Введите фамилию'); return; }
    if (firstName.trim() === '') { setError('Введите имя');     return; }

    setSaving(true);
    setError(null);
    setSavedMsg(null);

    const body = {
      lastName:   lastName.trim(),
      firstName:  firstName.trim(),
      middleName: middleName.trim(),
      isDismissed,
      departmentSkudId: departmentSkudId === '' ? null : Number(departmentSkudId),
      tabNumber: tabNumber.trim(),
      guid1c:    guid1c.trim(),
    };

    try {
      let saved;
      if (isEditNow) {
        await api.put(`/api/skud/persons/${savedSkudId}`, body);
        saved = {
          skudId:      savedSkudId,
          lastName:    body.lastName,
          firstName:   body.firstName,
          middleName:  body.middleName || null,
          isDismissed: body.isDismissed,
          pendingSync: true,
        };
        setSavedMsg('Изменения сохранены — синхронизация в очереди');
      } else {
        const r = await api.post('/api/skud/persons', body);
        saved = {
          skudId:      r.skudId,
          lastName:    body.lastName,
          firstName:   body.firstName,
          middleName:  body.middleName || null,
          isDismissed: body.isDismissed,
          pendingSync: !!r.pendingSync,
        };
        // Переключаем модалку в edit-режим и раскрываем блок пропусков.
        setSavedSkudId(r.skudId);
        setSavedMsg('Человек создан. Можно выдать пропуск.');
      }
      onSaved?.(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return createPortal((
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEditNow ? 'Человек в СКУД' : 'Новый человек в СКУД'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Фамилия *</label>
              <input className="sd-edit__input" value={lastName}
                     onChange={(e) => setLastName(e.target.value)} autoFocus />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Имя *</label>
              <input className="sd-edit__input" value={firstName}
                     onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Отчество</label>
              <input className="sd-edit__input" value={middleName}
                     onChange={(e) => setMiddleName(e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Подразделение</label>
              <select className="sd-edit__select"
                      value={departmentSkudId}
                      onChange={(e) => setDepartment(e.target.value)}>
                <option value="">— не указано —</option>
                {departments.map((d) => (
                  <option key={d.skud_id} value={d.skud_id}>{d.name}</option>
                ))}
              </select>
              <div className="sd-edit__hint">
                Определяет категорию человека в Orion. Дефолт подставляется
                из настроек СКУД (Настройки → СКУД → Маппинг подразделений).
              </div>
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Табельный номер</label>
              <input className="sd-edit__input" value={tabNumber}
                     onChange={(e) => setTabNumber(e.target.value)}
                     placeholder="необязательно" />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">GUID 1С</label>
              <input className="sd-edit__input mono" value={guid1c}
                     onChange={(e) => setGuid1c(e.target.value)}
                     placeholder="необязательно" />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={isDismissed}
                       onChange={(e) => setDismissed(e.target.checked)} />
                <span>Уволен / не активен</span>
              </label>
              <div className="sd-edit__hint">
                При включении — человек будет виден в списке, но перестанет
                открывать двери (в Orion статус is_dismissed=1).
              </div>
            </div>

            {!isEditNow && (
              <div className="sd-edit__field sd-edit__field--full">
                <div className="sd-edit__hint" style={{ background: 'var(--bg-subtle)', padding: 8, borderRadius: 6 }}>
                  После нажатия «Создать» запись уйдёт в Orion в течение
                  нескольких секунд. Дальше в этом же окне можно будет выдать
                  пропуск.
                </div>
              </div>
            )}

            {/* === Блок пропусков — доступен после того, как персона
                сохранена (в edit-mode или сразу после успешного create). === */}
            {isEditNow && savedSkudId != null && (
              <div className="sd-edit__field sd-edit__field--full"
                   style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
                <label className="sd-edit__label" style={{ marginBottom: 6 }}>Пропуска СКУД</label>
                <SkudPassesBlock skudPersonId={savedSkudId} />
              </div>
            )}
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {!error && savedMsg && (
              <div className="sd-edit__hint" style={{ color: 'var(--green-700, #15803d)' }}>
                {savedMsg}
              </div>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>
              {isEditNow ? 'Закрыть' : 'Отмена'}
            </button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEditNow ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  ), document.body);
}
